const API_BASE_URL = '/api';

interface ApiOptions extends RequestInit {
  requiresAuth?: boolean;
}

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const apiCall = async (endpoint: string, options: ApiOptions = {}) => {
  const { requiresAuth = true, ...fetchOptions } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers,
  };

  if (requiresAuth) {
    Object.assign(headers, getAuthHeaders());
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    // Try to extract error message from backend
    let errorMsg = `API call failed: ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData && errorData.message) {
        errorMsg = Array.isArray(errorData.message) ? errorData.message.join(', ') : errorData.message;
      }
    } catch {}
    throw new Error(errorMsg);
  }

  return response.json();
};

// Convenience methods
export const apiGet = (endpoint: string, options?: ApiOptions) =>
  apiCall(endpoint, { method: 'GET', ...options });

export const apiPost = (endpoint: string, data?: any, method: string = 'POST') =>
  apiCall(endpoint, {
    method,
    body: JSON.stringify(data),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

export const apiPut = (endpoint: string, data?: any, options?: ApiOptions) =>
  apiCall(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options,
  });

export const apiDelete = (endpoint: string, options?: ApiOptions) =>
  apiCall(endpoint, { method: 'DELETE', ...options });
